/**
 * 应付对账工作台 - 主页面 V2
 *
 * 设计文档: docs/05_workbench_architecture_v2.md
 *
 * 结构：
 *   WorkbenchHeader  — 批次身份 + 风险信号 + 关键金额 + 全局操作
 *   Tabs
 *     ├─ Tab 1: 批次汇总（航班×产品矩阵）
 *     ├─ Tab 2: 账单管理（IDS 列表 + 审核抽屉）
 *     └─ Tab 3: 订单核对（Phase 1 Mock）
 *   BusinessMaintenanceModal  — 页面级业务维护壳
 *   AgentSourcesModal  — 页面级 Agent 数据源管理 Modal
 */
import React, { useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, Badge, Modal, Form, Input, message } from "antd";
import { TableOutlined, FileTextOutlined, AuditOutlined } from "@ant-design/icons";
import { BatchProvider, useBatchContext } from "./context/BatchContext";
import WorkbenchHeader from "./components/WorkbenchHeader";
import InvoiceListTab from "./components/invoices/InvoiceListTab";
import InvoiceDetailDrawer from "./components/invoices/InvoiceDetailDrawer";
import BatchSummaryTab from "./components/summary/BatchSummaryTab";
import OrderReconciliationTab from "./components/orders/OrderReconciliationTab";
import UploadDrawer from "./components/upload/UploadDrawer";

// 页面级 Modal — 已迁移到工作台内部
import AgentSourcesModal from "./components/modals/AgentSourcesModal";
import ProductMaintenanceModal from "./components/shells/ProductMaintenanceModal";
import PromptMaintenanceModal from "./components/shells/PromptMaintenanceModal";
import BillingRulesMaintenanceModal from "./components/shells/BillingRulesMaintenanceModal";
import AirportSelector from "../../../components/common/AirportSelector";
import { getStandardCostTypeLabel } from "../constants";
import workbenchApi from "./services/workbenchApi";

import "./components/WorkbenchHeader.css";

// ---- 工作台内部组件 ----

const WorkbenchInner = () => {
    const [activeTab, setActiveTab] = useState("invoices");
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        statusSummary,
        refreshBatch,
        refreshAll,
        applyIdsStatusUpdate,
        batchNumber,
        // Modal 需要的数据
        vendorId,
        vendorName,
        standardCostType,
        airportCode,
        summary,
        agentSources,
        toggleSource,
        matrixLoading,
    } = useBatchContext();

    // 账单详情抽屉状态
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerRecord, setDrawerRecord] = useState(null);

    const handleOpenDrawer = useCallback((record) => {
        setDrawerRecord(record);
        setDrawerOpen(true);
    }, []);

    const handleCloseDrawer = useCallback(() => {
        setDrawerOpen(false);
        setDrawerRecord(null);
        refreshBatch();
    }, [refreshBatch]);

    const handleInvoiceStatusChange = useCallback((idsId, newStatus) => {
        applyIdsStatusUpdate(idsId, newStatus);
        refreshBatch();
    }, [applyIdsStatusUpdate, refreshBatch]);

    // Tab 1 矩阵中"来源"列点击账单号时的回调
    // 将 dataSourceId 转换为 InvoiceDetailDrawer 需要的 record 格式
    const handleInvoiceClickFromMatrix = useCallback(
        (dataSourceId) => {
            handleOpenDrawer({ id: dataSourceId });
        },
        [handleOpenDrawer],
    );

    // 上传抽屉状态
    const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
    const handleOpenUploadDrawer = useCallback(() => setUploadDrawerOpen(true), []);
    const handleCloseUploadDrawer = useCallback(() => setUploadDrawerOpen(false), []);
    const handleUploadSuccess = useCallback(() => {
        setUploadDrawerOpen(false);
        refreshBatch();
    }, [refreshBatch]);

    // 页面级 Modal 状态
    const [agentSourcesVisible, setAgentSourcesVisible] = useState(false);
    const [productMaintenanceVisible, setProductMaintenanceVisible] = useState(false);
    const [promptMaintenanceVisible, setPromptMaintenanceVisible] = useState(false);
    const [billingRulesMaintenanceVisible, setBillingRulesMaintenanceVisible] = useState(false);

    // 业务壳 Modal 的预筛条件：批次供应商 + 费用类型。
    const businessShellPredefinedFilters = useMemo(() => {
        const filters = {};
        if (vendorId) {
            filters.scenario_supplier_id = vendorId;
        }
        if (standardCostType) {
            filters.scenario_standard_cost_type = standardCostType;
        }
        return filters;
    }, [standardCostType, vendorId]);

    const businessShellPredefinedFilterLabels = useMemo(() => {
        const supplierLabel = vendorName || summary?.vendor_name || (vendorId ? String(vendorId) : undefined);
        const costType = standardCostType || summary?.standard_cost_type;
        return {
            scenario_supplier_label: supplierLabel,
            scenario_standard_cost_type_label: costType ? getStandardCostTypeLabel(costType) : undefined,
        };
    }, [standardCostType, summary?.standard_cost_type, summary?.vendor_name, vendorId, vendorName]);

    // 批次配置编辑 Modal
    const [batchConfigField, setBatchConfigField] = useState(null); // 'batch_number' | 'airport_code'
    const [batchConfigForm] = Form.useForm();
    const [batchConfigSubmitting, setBatchConfigSubmitting] = useState(false);

    const handleEditBatchConfig = useCallback((field) => {
        setBatchConfigField(field);
        batchConfigForm.setFieldsValue({
            batch_number: batchNumber || "",
            airport_code: airportCode || undefined,
        });
    }, [batchNumber, airportCode, batchConfigForm]);

    const handleBatchConfigSubmit = useCallback(async () => {
        try {
            const values = await batchConfigForm.validateFields();
            const newBatchNumber = values.batch_number?.trim();
            const newAirportCode = values.airport_code || "";

            const batchChanged = newBatchNumber !== (batchNumber || "");
            const airportChanged = newAirportCode !== (airportCode || "");

            if (!batchChanged && !airportChanged) {
                setBatchConfigField(null);
                return;
            }

            setBatchConfigSubmitting(true);
            await workbenchApi.updateBatchConfig({
                vendorId: vendorId,
                standardCostType,
                oldBatchNumber: batchNumber,
                newBatchNumber: batchChanged ? newBatchNumber : undefined,
                newAirportCode: airportChanged ? newAirportCode : undefined,
            });
            message.success("批次配置已更新");
            setBatchConfigField(null);

            // 如果批次号变了，用 react-router 更新 URL
            // setSearchParams 会触发 BatchContext 重新渲染，从而自动发起新的网络请求。
            if (batchChanged && newBatchNumber) {
                const newParams = new URLSearchParams(searchParams);
                newParams.set("batch", newBatchNumber);
                setSearchParams(newParams, { replace: true });
            } else {
                // 只有机场变了，URL 没变，手动触发刷新获取新的机场信息
                refreshAll();
            }
        } catch (err) {
            if (err.errorFields) return; // 表单验证失败
            message.error("更新失败: " + (err.response?.data?.detail || err.message));
        } finally {
            setBatchConfigSubmitting(false);
        }
    }, [batchConfigForm, batchNumber, airportCode, vendorId, standardCostType, refreshAll, searchParams, setSearchParams]);

    const tabItems = [
        {
            key: "summary",
            label: (
                <span>
                    <TableOutlined style={{ marginRight: 4 }} />
                    批次汇总
                </span>
            ),
            children: <BatchSummaryTab onInvoiceClick={handleInvoiceClickFromMatrix} />,
        },
        {
            key: "invoices",
            label: (
                <span>
                    <FileTextOutlined style={{ marginRight: 4 }} />
                    账单列表
                    {statusSummary.failed > 0 && <Badge count={statusSummary.failed} size="small" style={{ marginLeft: 6 }} />}
                </span>
            ),
            children: <InvoiceListTab onOpenDrawer={handleOpenDrawer} />,
        },
        {
            key: "orders",
            label: (
                <span>
                    <AuditOutlined style={{ marginRight: 4 }} />
                    订单核对
                </span>
            ),
            children: <OrderReconciliationTab />,
        },
    ];

    return (
        <>
            <div className="workbench-container">
                <WorkbenchHeader
                    onOpenProductMaintenance={() => setProductMaintenanceVisible(true)}
                    onOpenPromptMaintenance={() => setPromptMaintenanceVisible(true)}
                    onOpenBillingRulesMaintenance={() => setBillingRulesMaintenanceVisible(true)}
                    onOpenAgentSources={() => setAgentSourcesVisible(true)}
                    onOpenUploadDrawer={handleOpenUploadDrawer}
                    onEditBatchConfig={handleEditBatchConfig}
                />
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="middle" tabBarStyle={{ paddingLeft: 16, paddingRight: 16 }} />
            </div>

            {/* 账单详情抽屉（全局一个实例，由 Tab 2 触发） */}
            <InvoiceDetailDrawer open={drawerOpen} record={drawerRecord} onClose={handleCloseDrawer} onStatusChange={handleInvoiceStatusChange} />

            {/* 上传抽屉（追加模式 — 当前批次的配置已确定） */}
            <UploadDrawer open={uploadDrawerOpen} onClose={handleCloseUploadDrawer} onSuccess={handleUploadSuccess} presetBatchNumber={batchNumber} presetVendorId={vendorId} presetStandardCostType={standardCostType} presetAirportCode={airportCode} />

            {/* 页面级 Modal */}
            <AgentSourcesModal visible={agentSourcesVisible} onClose={() => setAgentSourcesVisible(false)} agentSources={agentSources} onToggleSource={toggleSource} loading={matrixLoading} />
            <ProductMaintenanceModal
                visible={productMaintenanceVisible}
                onClose={() => setProductMaintenanceVisible(false)}
                onSaved={() => {
                    setProductMaintenanceVisible(false);
                    refreshAll();
                }}
                predefinedFilters={businessShellPredefinedFilters}
                predefinedFilterLabels={businessShellPredefinedFilterLabels}
            />
            <PromptMaintenanceModal
                visible={promptMaintenanceVisible}
                onClose={() => setPromptMaintenanceVisible(false)}
                predefinedFilters={businessShellPredefinedFilters}
                predefinedFilterLabels={businessShellPredefinedFilterLabels}
            />
            <BillingRulesMaintenanceModal
                visible={billingRulesMaintenanceVisible}
                onClose={() => setBillingRulesMaintenanceVisible(false)}
                predefinedFilters={businessShellPredefinedFilters}
                predefinedFilterLabels={businessShellPredefinedFilterLabels}
            />

            {/* 批次配置编辑 Modal */}
            <Modal
                title={batchConfigField === "batch_number" ? "修改批次号" : "修改关联机场"}
                open={!!batchConfigField}
                onOk={handleBatchConfigSubmit}
                onCancel={() => setBatchConfigField(null)}
                confirmLoading={batchConfigSubmitting}
                okText="保存"
                cancelText="取消"
                destroyOnClose
                width={420}
            >
                <Form form={batchConfigForm} layout="vertical" style={{ marginTop: 16 }}>
                    {batchConfigField === "batch_number" && (
                        <Form.Item
                            name="batch_number"
                            label="批次号"
                            rules={[{ required: true, message: "批次号不能为空" }]}
                            extra="修改后该批次下所有账单记录的批次号将同步更新"
                        >
                            <Input placeholder="请输入新的批次号" maxLength={50} />
                        </Form.Item>
                    )}
                    {batchConfigField === "airport_code" && (
                        <Form.Item
                            name="airport_code"
                            label="关联机场"
                            extra="修改后该批次下所有账单记录的关联机场将同步更新"
                        >
                            <AirportSelector
                                valueType="iata_code"
                                placeholder="选择机场（留空表示清除）"
                                allowClear
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </>
    );
};

// ---- 页面入口 ----

const WorkbenchPage = () => {
    return (
        <BatchProvider>
            <WorkbenchInner />
        </BatchProvider>
    );
};

export default WorkbenchPage;
